import { useQueryClient } from "@tanstack/react-query";
import { useNotify, useTranslate, useUpdate } from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MISSING_DATE_LABEL, toValidDate, type DateLike } from "@/lib/safeDate";

import type { Task } from "../types";
import { TaskEdit } from "../tasks/TaskEdit";

const DATE_INPUT = /^\d{4}-\d{2}-\d{2}$/;

/** UTC calendar day of a real timestamp. Missing and invalid stay empty. */
const toDateInputValue = (iso: DateLike) => {
  if (typeof iso !== "string" || !toValidDate(iso)) return "";
  return iso.slice(0, 10);
};

export const NextActionButtons = ({
  task,
  onEdit,
}: {
  task: Task;
  /**
   * Opens an edit dialog the caller renders outside this tree.
   * A form inside `ActionTileControls` never submits: that wrapper cancels the click.
   */
  onEdit?: () => void;
}) => {
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();
  const translate = useTranslate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [nextDue, setNextDue] = useState(toDateInputValue(task.due_date));

  // Call-time onSuccess is skipped when this button unmounts before the
  // response (it sits inside the tile link). onSettled still runs.
  const settle =
    (successKey: string, errorKey: string, afterSuccess?: () => void) =>
    (_data: unknown, error: unknown) => {
      if (error) {
        notify(errorKey, { type: "error" });
        return;
      }
      notify(successKey, { type: "success" });
      afterSuccess?.();
      // Vandaag and Aandacht share this list. Refresh every tasks list.
      void queryClient.invalidateQueries({ queryKey: ["tasks", "getList"] });
    };

  const complete = () => {
    update(
      "tasks",
      {
        id: task.id,
        data: { done_date: new Date().toISOString() },
        previousData: task,
      },
      {
        onSettled: settle(
          "palmtree.actions.done_toast",
          "palmtree.actions.done_error",
        ),
      },
    );
  };

  const reschedule = () => {
    if (!DATE_INPUT.test(nextDue)) return;
    update(
      "tasks",
      {
        id: task.id,
        data: {
          due_date: new Date(`${nextDue}T12:00:00`).toISOString(),
          due_has_time: false,
        },
        previousData: task,
      },
      {
        onSettled: settle(
          "palmtree.actions.moved_toast",
          "palmtree.actions.moved_error",
          () => setOpen(false),
        ),
      },
    );
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" disabled={isPending} onClick={complete}>
          {translate("palmtree.actions.done")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={isPending}
          onClick={() => {
            setNextDue(toDateInputValue(task.due_date));
            setOpen(true);
          }}
        >
          {translate("palmtree.actions.move")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={isPending}
          onClick={() => (onEdit ? onEdit() : setEditOpen(true))}
        >
          {translate("resources.tasks.action.edit")}
        </Button>
      </div>
      {onEdit ? null : (
        <TaskEdit
          taskId={task.id}
          open={editOpen}
          close={() => setEditOpen(false)}
        />
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate("palmtree.actions.move_title")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {!toValidDate(task.due_date) ? (
              <p className="text-sm text-muted-foreground">
                {MISSING_DATE_LABEL}
              </p>
            ) : null}
            <Label htmlFor={`due-${task.id}`}>
              {translate("palmtree.actions.new_date")}
            </Label>
            <Input
              id={`due-${task.id}`}
              type="date"
              value={nextDue}
              onChange={(event) => setNextDue(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              {translate("palmtree.contact.cancel")}
            </Button>
            <Button
              type="button"
              onClick={reschedule}
              disabled={isPending || !DATE_INPUT.test(nextDue)}
            >
              {translate("palmtree.contact.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
